import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetCitiesQuery, useDeleteCityMutation, useGetCountryQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { formatDateOnly } from '../../../utils/helpers';
import { showSuccess, showError } from '../../../utils/toast';

const AdminCities: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const country_id = searchParams.get('country_id');
  const numericCountryId = country_id ? parseInt(country_id, 10) : null;
  const { data: countryData } = useGetCountryQuery(numericCountryId as number, {
    skip: !numericCountryId,
  });
  const countryName = countryData?.data?.name;
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, error, refetch } = useGetCitiesQuery({ search, daterange, page, country_id });
  const [deleteCity] = useDeleteCityMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteCity(id).unwrap();
      showSuccess(result.message || 'City deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete city. Please try again.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading cities</div>;
  }

  const cities = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {countryName ? `Cities in ${countryName}` : 'Cities'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {countryName ? `Cities in ${countryName}` : 'Cities'}
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search City Name.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <DateRangePicker
                          value={daterange}
                          onChange={setDaterange}
                          placeholder="Date Range"
                        />
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">Clear</a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
                <div className="card-toolbar">
                  <a href="#" className="btn btn-base" onClick={(e) => { 
                    e.preventDefault(); 
                    const url = country_id ? `/admin/cities/add?country_id=${country_id}` : '/admin/cities/add';
                    navigate(url); 
                  }}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add New City
                  </a>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Name</th>
                        <th>Name Arabic</th>
                        <th>Status</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {cities.data.map((city: any, index: number) => (
                        <tr key={city.id}>
                          <td>{(cities.current_page - 1) * 15 + index + 1}</td>
                          <td>{city.name}</td>
                          <td>{city.name_ar || 'N/A'}</td>
                          <td>
                            <span className={`badge badge-light-${city.status === 1 ? 'success' : 'danger'}`}>
                              {city.status === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{formatDateOnly(city.created_at)}</td>
                          <td className="text-center">
                            <ActionButtons
                              onView={() => navigate(`/admin/cities/${city.id}`)}
                              onEdit={() => navigate(`/admin/cities/${city.id}/edit`)}
                              onDelete={() => handleDelete(city.id)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {cities.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={cities.current_page}
                      lastPage={cities.last_page}
                      total={cities.total || 0}
                      perPage={cities.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCities;





